import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import type { Session } from '@supabase/supabase-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { env } from '../lib/env.js';
import { logger } from '../lib/logger.js';
import { isNativePlatform } from '../platform/device.js';
import { describeOAuthCallback } from './oauthCallback.js';
import { getSupabase } from './supabaseClient.js';

export type AuthStatus = 'loading' | 'signed_in' | 'signed_out' | 'unconfigured';

export interface AuthState {
  status: AuthStatus;
  userId: string | null;
  session: Session | null;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

/**
 * Se exporta para que los tests puedan montar el arbol con una sesion ficticia
 * sin tocar la red. La aplicacion siempre usa `AuthProvider`.
 */
export const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const supabase = getSupabase();
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthStatus>(supabase ? 'loading' : 'unconfigured');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supabase) return;

    let active = true;
    // `getSession` espera a la inicializacion del cliente, que es donde
    // `detectSessionInUrl` canjea el `?code=` de PKCE. Al resolverse, el canje
    // ya ha terminado: con exito (hay sesion y la URL esta limpia) o sin el.
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setStatus(data.session ? 'signed_in' : 'signed_out');

      if (data.session === null && !isNativePlatform()) {
        const detalle = describeOAuthCallback(globalThis.location.search);
        if (detalle !== null) {
          setError(detalle);
          logger.error('auth', 'OAUTH_CALLBACK_UNRESOLVED', 'Retorno de OAuth sin sesion', {
            // Nunca el valor de la clave: solo cuantos problemas de forma se detectaron.
            problemasDeConfiguracion: env.problems.length,
          });
        }
      }
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event, next) => {
      logger.info('auth', 'AUTH_STATE_CHANGE', 'Cambio de estado de sesion', { event });
      setSession(next);
      setStatus(next ? 'signed_in' : 'signed_out');
      if (next) setError(null);
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, [supabase]);

  // Android: el retorno de Google llega por deep link con esquema propio.
  useEffect(() => {
    if (!supabase || !isNativePlatform()) return;

    const handle = CapacitorApp.addListener('appUrlOpen', ({ url }) => {
      void (async () => {
        try {
          const parsed = new URL(url);
          const code = parsed.searchParams.get('code');
          if (!code) return;
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) throw new Error(exchangeError.message);
          await Browser.close();
        } catch (caught) {
          const message = caught instanceof Error ? caught.message : String(caught);
          setError(message);
          logger.error('auth', 'DEEPLINK_EXCHANGE_FAILED', 'Fallo el retorno de OAuth', {
            message,
          });
        }
      })();
    });

    return () => {
      void handle.then((listener) => listener.remove());
    };
  }, [supabase]);

  const signInWithGoogle = useCallback(async () => {
    if (!supabase) {
      setError('Supabase no esta configurado en este dispositivo.');
      return;
    }
    setError(null);

    const redirectTo = isNativePlatform()
      ? `${env.oauthRedirectScheme}://auth-callback`
      : `${globalThis.location.origin}/`;

    const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        skipBrowserRedirect: isNativePlatform(),
      },
    });

    if (oauthError) {
      setError(oauthError.message);
      logger.error('auth', 'OAUTH_START_FAILED', 'No se pudo iniciar OAuth', {
        message: oauthError.message,
      });
      return;
    }

    if (isNativePlatform() && data.url) {
      await Browser.open({ url: data.url });
    }
  }, [supabase]);

  const signOut = useCallback(async () => {
    await supabase?.auth.signOut();
  }, [supabase]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      userId: session?.user.id ?? null,
      session,
      error,
      signInWithGoogle,
      signOut,
    }),
    [status, session, error, signInWithGoogle, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return context;
}
